import { SurveyJSViewMode } from "@parthenon-management/pillar-admin/src/constants";
import {
  type CompleteEvent,
  Model,
  QuestionPanelDynamicModel,
  type SurveyModel,
} from "survey-core";
import { RegisterCertificationAttributionMultipleQuestionType } from "shared/components/survey-js-form/questions/certification-attribution/CertificationAttributionMultipleQuestion";
import { RegisterPillarChoiceDropdownComponent } from "shared/components/survey-js-form/questions/choice/PillarChoiceDropdownComponent";
import { registerChoiceQuestion } from "shared/components/survey-js-form/questions/choice/registerChoiceQuestion";
import {
  registerChoicesFromPillarProperties,
  setupChoicesFromPillar,
} from "shared/components/survey-js-form/questions/choice/setupChoicesFromPillar";
import { registerDegreeQuestionOnSubmit } from "shared/components/survey-js-form/questions/degree/degreeQuestionOnSubmit";
import { degree_question } from "shared/components/survey-js-form/questions/degree/registerDegreeQuestion";
import {
  applyDropdownCustomizations,
  registerDropdownCustomizations,
} from "shared/components/survey-js-form/questions/dropdown/customizeDropdown";
import { RegisterEducationInstanceMultipleQuestionType } from "shared/components/survey-js-form/questions/education/EducationInstanceMultipleQuestion";
import { RegisterEducationInstanceQuestionType } from "shared/components/survey-js-form/questions/education/EducationInstanceQuestion";
import { fileQuestionProperties } from "shared/components/survey-js-form/questions/file-upload/fileQuestionProperties";
import { fileUploadSetup } from "shared/components/survey-js-form/questions/file-upload/fileUploadSetup";
import { profileInstitutionTypeQuestion } from "shared/components/survey-js-form/questions/institution-type/question";
import { RegisterLoginDetailsQuestionType } from "shared/components/survey-js-form/questions/login-details/LoginDetailsQuestion";

import { RegisterObfuscateQuestionComponent } from "shared/components/survey-js-form/questions/obfuscate/ObfuscateQuestionComponent";
import {
  optionalMobileProperties,
  applyOptionalMobileLogic,
} from "shared/components/survey-js-form/questions/optional-mobile/optionalMobileProperties";
import {
  applyPanelDynamicCustomizations,
  registerPanelDynamicCustomizations,
} from "shared/components/survey-js-form/questions/paneldynamic/customizePanelDynamic";
import { ProductsSurveyJSQuestion } from "shared/components/survey-js-form/questions/products/ProductsSurveyJSQuestion";
import { registerProfileAddressContactInformationQuestion } from "shared/components/survey-js-form/questions/profile-address-contact-information/registerProfileAddressContactInformationQuestion";
import { ProfileSearchMultipleQuestion } from "shared/components/survey-js-form/questions/profile-search/ProfileSearchMultipleQuestion";
import { RegisterProfileSearchQuestionComponent } from "shared/components/survey-js-form/questions/profile-search/ProfileSearchQuestionComponent";
import { profileSearchValidationExpressions } from "shared/components/survey-js-form/questions/profile-search/profileSearchValidationExpressions";
import { surveyInitProfileSearchQuestion } from "shared/components/survey-js-form/questions/profile-search/surveyInitProfileSearchQuestion";
import { registerProfileTypeQuestion } from "shared/components/survey-js-form/questions/profile-type/registerProfileTypeQuestion";
import { onTextMarkdownApplyHtmlToSurveyContents } from "shared/components/survey-js-form/questions/rich-text/onTextMarkdownApplyHtmlToSurveyContents";
import { RegisterRichTextDisplayComponent } from "shared/components/survey-js-form/questions/rich-text/RichTextDisplayComponent";
import { globalValidationProperties } from "shared/components/survey-js-form/questions/text-box/globalValidationProperties";
import { RegisterReactTextboxComponent } from "shared/components/survey-js-form/questions/text-box/ReactTextboxComponent";
import { type SessionView } from "shared/mappers/database/session/session";
import { type FormDesign } from "shared/components/survey-js-form/types/IFormDesign";
import { type WorkflowInstanceView } from "shared/mappers/database/workflow/workflow-instance";
import { type WorkflowInstancePayloadValueBase } from "shared/workflow/workflow-instance-payload";
import { DefaultLight } from "survey-core/themes";

export type InitializeSurveyModelProps = {
  session: SessionView;
  baseUrl?: string;
  onComplete: (sender: SurveyModel, options: CompleteEvent) => Promise<void>;
  formDesign: FormDesign;
  formData?: WorkflowInstancePayloadValueBase;
  workflowInstance?: WorkflowInstanceView;
  worklflowId?: number;
  isReadOnly?: boolean;
  overrideCompleteButtonText?: string;
  deviceType?: string;
};

declare module "survey-core" {
  interface PanelModel {
    panelTag?: string;
  }
}

export const initializeSurveyModel = async ({
  session,
  formDesign,
  formData,
  workflowInstance,
  isReadOnly,
  baseUrl,
  onComplete,
  overrideCompleteButtonText,
  deviceType,
}: InitializeSurveyModelProps) => {
  globalValidationProperties();
  optionalMobileProperties();
  fileQuestionProperties();
  await Promise.all([
    registerChoiceQuestion("cospeaker_roles", session),
    registerChoiceQuestion("research_type", session),
    registerChoiceQuestion("primary_category", session),
    registerChoiceQuestion("expertise", session),
    registerChoiceQuestion("secondary_category", session),
    ProductsSurveyJSQuestion({ session }),
    RegisterProfileSearchQuestionComponent(session, baseUrl!),
    profileInstitutionTypeQuestion(session.society?.institutionType ?? []),
    registerProfileTypeQuestion(),
    degree_question(),
    ProfileSearchMultipleQuestion({ session }),
    RegisterObfuscateQuestionComponent(),
    RegisterLoginDetailsQuestionType(),
    RegisterEducationInstanceQuestionType(session, baseUrl!),
    RegisterEducationInstanceMultipleQuestionType({ session }),
    RegisterCertificationAttributionMultipleQuestionType({ session }),
  ]);
  RegisterReactTextboxComponent();

  RegisterPillarChoiceDropdownComponent(session);
  RegisterRichTextDisplayComponent();
  //This is a dependent question (on emailOption), and must be run after the concurrent promises.all
  await registerProfileAddressContactInformationQuestion({
    session,
  });
  // Ensure custom dropdown property is registered BEFORE creating the model so that
  // the JSON formDesign property `auto_select_single_choice` is not stripped by SurveyJS.
  // If we wait until after instantiation, SurveyJS will ignore unregistered properties and
  // our later customization pass will not detect the flag, causing the test to fail.
  registerDropdownCustomizations();
  // Register paneldynamic custom property prior to model creation so JSON formDesign retains it
  registerPanelDynamicCustomizations();
  // Same reason: `pillar_choices_question_name` has to be a known property
  // before the form JSON is parsed, or SurveyJS strips it and the question
  // never gets pointed at its choices endpoint.
  if (session.society) {
    registerChoicesFromPillarProperties({
      society: session.society,
      baseUrl,
    });
  }
  const model = new Model(formDesign);
  model.applyTheme(DefaultLight);
  onTextMarkdownApplyHtmlToSurveyContents({ surveyModel: model });
  profileSearchValidationExpressions();

  const q = model.getQuestionByName("participants");
  const panelDynamic = q?.contentQuestion as QuestionPanelDynamicModel;

  if (panelDynamic) {
    setTimeout(() => {
      panelDynamic.panels?.forEach((panel) => {
        panel.panelTag = "profilesearchmultiple";
      });
    }, 0);
  }

  surveyInitProfileSearchQuestion({
    surveyModel: model,
    session,
  });

  if (overrideCompleteButtonText !== undefined) {
    model.completeText = overrideCompleteButtonText;
  }

  //Tags if Admin all tags, else only current profile tags
  const tags = session.adminMode
    ? (session.society?.tags?.map((tag) => tag.tagName) ?? [])
    : session.profile?.tags?.map((tag) => tag.tagName);
  model.setVariable("societyTags", tags);
  model.setVariable("workflowInstanceStatus", workflowInstance?.status);
  model.setVariable(
    "workflowInstanceState",
    workflowInstance?.currentStateName,
  );

  // Set device type
  if (deviceType) {
    model.setVariable("deviceType", deviceType);
  }

  //IgnoreValidation admin mode
  if (session.adminMode) {
    model.ignoreValidation = true;
  }

  if (session.society) {
    setupChoicesFromPillar({
      society: session.society!,
      surveyModel: model,
      baseUrl: baseUrl,
    });
  }

  if (session.society?.societyId) {
    await fileUploadSetup({
      societyId: Number(session.society.societyId),
      profileId: session.profile?.profileId,
      model,
    });
  }

  model.clearInvisibleValues = "none";

  if (isReadOnly) {
    model.mode = SurveyJSViewMode.Read_Only;
  }
  if (model && formData && Object.keys(formData as object).length > 0) {
    model.mergeData(formData);
  }

  // Register dropdown custom property and apply customizations (opt-in per question)
  applyDropdownCustomizations(model);
  // Apply paneldynamic customizations (opt-in via copyValueFromFirstPanel list)
  applyPanelDynamicCustomizations(model);
  // Apply optional mobile logic
  applyOptionalMobileLogic(model);
  registerDegreeQuestionOnSubmit({ model, formData });

  model.onValidateQuestion.add((sender: SurveyModel, options) => {
    const question = options.question;
    const wordLimit = question.getPropertyValue("wordLimit");
    const withSpaces = question.getPropertyValue("withSpaces");
    if (!wordLimit) {
      return;
    } else {
      const text = question.value;
      if (text) {
        let wordCount;
        if (withSpaces) {
          wordCount =
            text.split(/\s+/).length - 1 + (text.match(/\s/g) || []).length;
        } else {
          wordCount = text.trim().split(/\s+/).length;
        }
        if (wordCount > wordLimit) {
          options.error = `Word limit exceeded. Maximum ${wordLimit} words allowed. You have used ${wordCount} words ${
            withSpaces && "including the spaces"
          }.`;
        }
      }
    }
  });
  model.onComplete.add(onComplete);
  return model;
};
